<script>
import Datepicker from "vue3-datepicker";
import { ref } from "vue";

import CKEditor from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import Multiselect from "@vueform/multiselect";
import draggable from "vuedraggable";

import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import useVuelidate from "@vuelidate/core";
import categorySvc from "@/services/category";
import festivalSvc from "@/services/festival";
import regionSvc from "@/services/region";
import provinceSvc from "@/services/province";
import officeSvc from "@/services/office";
import tagSvc from "@/services/tag";
import blogSvc from "@/services/blog";
import tatApiSvc from "@/services/tat-api";
import { toast } from "vue3-toastify";
import { DateTime } from "luxon";
import MyUploadAdapter from "@/services/upload-adapter";
import { required, helpers, requiredIf } from "@vuelidate/validators";

/**
 * Form validation component
 */
export default {
  page: {
    title: "Manage Event",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Datepicker,
    Layout,
    PageHeader,
    ckeditor: CKEditor.component,
    Multiselect,
    draggable,
  },
  data() {
    return {
      title: "Manage Event",
      role:
        localStorage.getItem("userInfo") !== null
          ? JSON.parse(localStorage.getItem("userInfo")).role
          : "user",
      editorConfig: {
        mediaEmbed: {
          elementName: "iframe",
        },
      },
      items: [
        {
          text: "Event",
          href: "list",
        },
        {
          text: "Manage Event",
          active: true,
        },
      ],
      hiddenButtonSave: true,
      isDisabled: false,
      categories: [],
      typesOption: [
        {
          label: "Event",
          value: "event",
        },
        {
          label: "News",
          value: "news",
        },
      ],
      categoriesOption: [],
      festivalsOption: [],
      regionsOption: [],
      provincesOption: [],
      officesOption: [],
      tagsOptions: [],
      blogForm: {
        uuid: null,
        tatApiEventId: "",
        tatApiUpdatedAt: "",
        tatApiChangeData: false,
        aptApiEventId: "",
        aptApiUpdatedAt: "",
        aptApiChangeData: false,
        type: "",
        category: "",
        festivalUUID: "",
        regionId: "",
        provinceCode: "",
        officeUUID: "",
        name: "",
        location: "",
        locationLink: "",
        detail: "",
        isTh: true,
        isEn: false,
        nameEn: "",
        locationEn: "",
        locationLinkEn: "",
        detailEn: "",
        startDate: "",
        endDate: "",
        dateRemark: "",
        startTime: "",
        endTime: "",
        tag: [],
        statusCode: "",
        statusHistory: "",
        revisions: "",
        images: [],
        thumbnail: "",
        ticketType: "",
        ticketLink: "",
        facebookName: "",
        facebookLink: "",
        instagramName: "",
        instagramLink: "",
        youtubeName: "",
        youtubeLink: "",
        websiteName: "",
        websiteLink: "",
        phone: "",
      },
      isFacebook: false,
      isInstagram: false,
      isYoutube: false,
      isWebsite: false,
      isPhone: false,
      submitted: false,
      submit: false,
      editor: ClassicEditor,
      isSendApprove: false,
      showModalApprove: false,
      approve: {
        remark: "",
      },
      showModalReject: false,
      reject: {
        remark: "",
      },
      showModalRequestEdit: false,
      requestEdit: {
        remark: "",
      },
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    lowerDate() {
      return this.blogForm.startDate !== ""
        ? this.blogForm.startDate
        : new Date();
    },
  },
  validations: {
    blogForm: {
      type: {
        required: requiredIf(function () {
          return this.blogForm.type === "";
        }),
      },
      category: {
        required: requiredIf(function () {
          return this.blogForm.category === "";
        }),
      },
      regionId: {
        required: requiredIf(function () {
          return (
            this.blogForm.type === "event" && this.blogForm.regionId === ""
          );
        }),
      },
      provinceCode: {
        required: requiredIf(function () {
          return (
            this.blogForm.type === "event" && this.blogForm.provinceCode === ""
          );
        }),
      },
      name: {
        required: requiredIf(function () {
          return this.blogForm.isTh && this.blogForm.name === "";
        }),
      },
      location: {
        required: requiredIf(function () {
          return (
            this.blogForm.isTh &&
            this.blogForm.type === "event" && this.blogForm.location === ""
          );
        }),
      },
      locationLink: {
        required: requiredIf(function () {
          return (
            this.blogForm.isTh &&
            this.blogForm.type === "event" && this.blogForm.locationLink === ""
          );
        }),
      },
      detail: {
        required: requiredIf(function () {
          return this.blogForm.isTh && this.blogForm.detail === "";
        }),
      },
      nameEn: {
        required: requiredIf(function () {
          return this.blogForm.isEn && this.blogForm.nameEn === "";
        }),
      },
      locationEn: {
        required: requiredIf(function () {
          return (
            this.blogForm.isEn &&
            this.blogForm.type === "event" &&
            this.blogForm.locationEn === ""
          );
        }),
      },
      locationLinkEn: {
        required: requiredIf(function () {
          return (
            this.blogForm.isEn &&
            this.blogForm.type === "event" &&
            this.blogForm.locationLinkEn === ""
          );
        }),
      },
      detailEn: {
        required: requiredIf(function () {
          return this.blogForm.isEn && this.blogForm.detailEn === "";
        }),
      },
      startDate: {
        required: requiredIf(function () {
          return (
            this.blogForm.type === "event" && this.blogForm.startDate === ""
          );
        }),
      },
      endDate: {
        required: requiredIf(function () {
          return this.blogForm.type === "event" && this.blogForm.endDate === "";
        }),
      },
      thumbnail: {
        required: requiredIf(function () {
          return this.blogForm.thumbnail.length === 0;
        }),
      },
      ticketType: {
        required: requiredIf(function () {
          return (
            this.blogForm.type === "event" && this.blogForm.ticketType === ""
          );
        }),
      },
      facebookName: {
        required: requiredIf(function () {
          return this.isFacebook && this.blogForm.facebookName === "";
        }),
      },
      facebookLink: {
        required: requiredIf(function () {
          return this.isFacebook && this.blogForm.facebookLink === "";
        }),
      },
      instagramName: {
        required: requiredIf(function () {
          return this.isInstagram && this.blogForm.instagramName === "";
        }),
      },
      instagramLink: {
        required: requiredIf(function () {
          return this.isInstagram && this.blogForm.instagramLink === "";
        }),
      },
      youtubeName: {
        required: requiredIf(function () {
          return this.isYoutube && this.blogForm.youtubeName === "";
        }),
      },
      youtubeLink: {
        required: requiredIf(function () {
          return this.isYoutube && this.blogForm.youtubeLink === "";
        }),
      },
      websiteName: {
        required: requiredIf(function () {
          return this.isWebsite && this.blogForm.websiteName === "";
        }),
      },
      websiteLink: {
        required: requiredIf(function () {
          return this.isWebsite && this.blogForm.websiteLink === "";
        }),
      },
      phone: {
        required: requiredIf(function () {
          return this.isPhone && this.blogForm.phone === "";
        }),
      },
    },
    reject: {
      remark: {
        required: helpers.withMessage("Remark is required", required),
      },
    },
    // requestEdit: {
    //   remark: {
    //     required: helpers.withMessage("Remark is required", required),
    //   },
    // },
  },
  mounted() {
    this.getCategory();
    this.getFestival();
    this.getRegion();
    this.getOffice();
  },
  methods: {
    getCategory() {
      categorySvc
        .getAll()
        .then((resp) => {
          this.categories = resp.data.data;
          this.categoriesOption = resp.data.data.map((category) => {
            return {
              value: category.uuid,
              label: `${category.name} (${category.nameEn})`,
            };
          });
        })
        .finally(() => {
          this.getTag();
        });
    },
    getFestival() {
      festivalSvc.getDropdown().then((resp) => {
        this.festivalsOption = resp.data.data.map((festival) => {
          return { value: festival.value, label: `${festival.text}` };
        });
      });
    },
    getRegion() {
      regionSvc.getDropdown().then((resp) => {
        this.regionsOption = resp.data.data.map((region) => {
          return { value: region.value, label: `${region.text}` };
        });
      });
    },
    getProvince(region) {
      if (region !== this.blogForm.regionId) {
        this.provincesOption = [];
        this.blogForm.provinceCode = "";
      }
      provinceSvc.getDropdown(region).then((resp) => {
        this.provincesOption = resp.data.data.map((province) => {
          return { value: province.value, label: `${province.text}` };
        });
      });
    },
    getOffice() {
      officeSvc.getDropdown().then((resp) => {
        this.officesOption = resp.data.data.map((office) => {
          return { value: office.value, label: `${office.text}` };
        });
      });
    },
    getTag() {
      tagSvc
        .getDropdown()
        .then((resp) => {
          this.tagsOptions = resp.data.data.map((tag) => {
            return { value: tag.value, label: `${tag.text}` };
          });
        })
        .finally(() => {
          if (this.$route.query.uuid) {
            this.get(this.$route.query.uuid);
          } else if (this.$route.query.duplicate) {
            this.getDuplicate(this.$route.query.duplicate);
          } else if (this.$route.query.ref) {
            this.getEventTatic(this.$route.query.ref);
          } else if (this.$route.query.apt) {
            this.getAptDetail(this.$route.query.apt);
          } else {
            this.hiddenButtonSave = false;
          }
        });
    },
    get(uuid) {
      blogSvc
        .getDetail(uuid)
        .then((resp) => {
          const blog = resp.data.data;

          this.isFacebook = blog.facebookName !== "";
          this.isInstagram = blog.instagramName !== "";
          this.isYoutube = blog.youtubeName !== "";
          this.isWebsite = blog.websiteName !== "";
          this.isPhone = blog.phone !== "";

          this.blogForm = blog;
          this.blogForm.isTh = !!(blog.name && blog.name !== "");
          this.blogForm.category = blog.categoryUUID;
          this.blogForm.festivalUUID = blog.festivalUUID;
          this.blogForm.officeUUID = blog.officeUUID || "";
          this.blogForm.images = blog.images === null ? [] : blog.images;
          this.blogForm.startDate = blog.startDate
            ? ref(new Date(blog.startDate))
            : "";
          this.blogForm.endDate = blog.endDate
            ? ref(new Date(blog.endDate))
            : "";

          if (blog.tags) {
            this.blogForm.tag = blog.tags.map((tag) => {
              return tag.tagCode;
            });
          }

          if (
            [
              "pending_verify",
              "approved",
              "publish",
              "unpublish",
              "editing",
            ].includes(blog.statusCode)
          ) {
            this.isDisabled = true;
          } else {
            this.hiddenButtonSave = false;
            this.isDisabled = false;
          }
        })
        .finally(() => {
          if (this.blogForm.tatApiEventId) {
            this.getEventTatic(this.blogForm.tatApiEventId);
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("data not found", {
            autoClose: localStorage.getItem("toastCountdown"),
          });

          this.$router.push("list");
        });
    },
    getDuplicate(uuid) {
      blogSvc
        .getDetail(uuid)
        .then((resp) => {
          const blog = resp.data.data;

          this.isFacebook = blog.facebookName !== "";
          this.isInstagram = blog.instagramName !== "";
          this.isYoutube = blog.youtubeName !== "";
          this.isWebsite = blog.websiteName !== "";
          this.isPhone = blog.phone !== "";

          // Copy blog data without uuid to create a new record
          this.blogForm = {
            uuid: null, // Reset uuid for new record
            tatApiEventId: blog.tatApiEventId || "",
            tatApiUpdatedAt: blog.tatApiUpdatedAt || "",
            tatApiChangeData: blog.tatApiChangeData || false,
            aptApiEventId: blog.aptApiEventId || "",
            aptApiUpdatedAt: blog.aptApiUpdatedAt || "",
            aptApiChangeData: blog.aptApiChangeData || false,
            type: blog.type || "",
            category: blog.categoryUUID || "",
            festivalUUID: blog.festivalUUID || "",
            regionId: blog.regionId || "",
            provinceCode: blog.provinceCode || "",
            officeUUID: blog.officeUUID || "",
            name: blog.name || "",
            location: blog.location || "",
            locationLink: blog.locationLink || "",
            detail: blog.detail || "",
            isTh: blog.isTh !== undefined ? blog.isTh : true,
            isEn: blog.isEn || false,
            nameEn: blog.nameEn || "",
            locationEn: blog.locationEn || "",
            locationLinkEn: blog.locationLinkEn || "",
            detailEn: blog.detailEn || "",
            startDate: blog.startDate
              ? ref(new Date(blog.startDate))
              : "",
            endDate: blog.endDate ? ref(new Date(blog.endDate)) : "",
            dateRemark: blog.dateRemark || "",
            startTime: blog.startTime || "",
            endTime: blog.endTime || "",
            tag: blog.tags
              ? blog.tags.map((tag) => {
                  return tag.tagCode;
                })
              : [],
            statusCode: "",
            statusHistory: "",
            revisions: "",
            images: blog.images === null ? [] : blog.images,
            thumbnail: blog.thumbnail || "",
            ticketType: blog.ticketType || "",
            ticketLink: blog.ticketLink || "",
            facebookName: blog.facebookName || "",
            facebookLink: blog.facebookLink || "",
            instagramName: blog.instagramName || "",
            instagramLink: blog.instagramLink || "",
            youtubeName: blog.youtubeName || "",
            youtubeLink: blog.youtubeLink || "",
            websiteName: blog.websiteName || "",
            websiteLink: blog.websiteLink || "",
            phone: blog.phone || "",
          };

          this.hiddenButtonSave = false;
          this.isDisabled = false;
        })
        .finally(() => {
          if (this.blogForm.tatApiEventId) {
            this.getEventTatic(this.blogForm.tatApiEventId);
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("data not found", {
            autoClose: localStorage.getItem("toastCountdown"),
          });

          this.$router.push("list");
        });
    },
    getAptDetail(documentId) {
      tatApiSvc
        .getAptDetail(documentId)
        .then((resp) => {
          if (resp?.data?.data) {
            const d = resp.data.data;
            this.blogForm.type = "event";
            this.blogForm.aptApiEventId = d.documentId || "";
            this.blogForm.aptApiUpdatedAt = d.updatedAt || "";
            this.blogForm.aptApiChangeData =
              this.blogForm.aptApiUpdatedAt !== d.updatedAt;
            this.blogForm.name = d.eventFestivalName || "";
            this.blogForm.detail = d.details || "";
            this.blogForm.startDate = d.eventStartDate
              ? ref(new Date(d.eventStartDate))
              : "";
            this.blogForm.endDate = d.eventEndDate
              ? ref(new Date(d.eventEndDate))
              : "";
            this.blogForm.thumbnail =
              d.imageBanner?.formats?.small?.url ||
              d.imageBanner?.url ||
              "";
            this.blogForm.locationLink = d.googleMapUrl || "";
            this.blogForm.phone = d.contactNumber || "";
            this.isPhone = this.blogForm.phone !== "";

            if (d.facebook) {
              this.isFacebook = true;
              this.blogForm.facebookLink = d.facebook;
            }
            if (d.instagram) {
              this.isInstagram = true;
              this.blogForm.instagramLink = d.instagram;
            }
            if (d.youtube) {
              this.isYoutube = true;
              this.blogForm.youtubeLink = d.youtube;
            }
            if (d.website) {
              this.isWebsite = true;
              this.blogForm.websiteLink = d.website;
            }

            if (d.eventType) {
              tatApiSvc.getCategory(d.eventType).then((response) => {
                const p = response.data?.data;
                if (p) {
                  this.blogForm.category = p.uuid;
                }
              });
            }

            if (d.province) {
              tatApiSvc.getProvince(d.province).then((response) => {
                const p = response.data?.data;
                if (p) {
                  this.blogForm.regionId = p.regionId;
                  this.getProvince(p.regionId);
                  this.blogForm.provinceCode = p.code;
                }
              });
            }

            if (d.imageGallery?.length) {
              this.blogForm.images = d.imageGallery.map((img) => ({
                uuid: img.url,
                url: img.formats?.medium?.url || img.url,
              }));
            }

            this.hiddenButtonSave = false;
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("data not found", {
            autoClose: localStorage.getItem("toastCountdown"),
          });
          this.$router.push(`../tat/list`);
        });
    },
    getEventTatic(id) {
      const page = 1;
      const perPage = 1;
      tatApiSvc
        .getAllTatic(page, perPage, id)
        .then((resp) => {
          if (resp?.data?.data?.data?.length) {
            const data = resp.data.data.data[0];
            this.blogForm.tatApiChangeData =
              this.blogForm.tatApiUpdatedAt !== data.dateUpdated;
            this.blogForm.tatApiUpdatedAt = data.dateUpdated;
            if (!this.blogForm.uuid) {
              this.blogForm.type = "event";
              this.blogForm.tatApiEventId = `${data.slug}`;
              this.blogForm.tatApiUpdatedAt = data.dateUpdated;
              this.blogForm.name = data.name;
              if (data.nameEn) {
                this.blogForm.isEn = true;
                this.blogForm.nameEn = data.nameEn;
                this.blogForm.detailEn = data.descriptionEn;
              }
              this.blogForm.startDate = data.startDate
                ? ref(new Date(data.startDate))
                : "";
              this.blogForm.endDate = data.endDate
                ? ref(new Date(data.endDate))
                : "";
              this.blogForm.thumbnail = data.thumbImage ? data.thumbImage : "";
              this.blogForm.detail = data.description || "";

              this.blogForm.phone = data.phone || "";
              this.isPhone = this.blogForm.phone !== "";

              if (data.tatEventTypeName) {
                const categoryName = data.tatEventTypeName;
                if (categoryName) {
                  tatApiSvc.getCategory(categoryName).then((response) => {
                    const p = response.data?.data;
                    if (p) {
                      this.blogForm.category = p.uuid;
                    }
                  });
                }
              }

              if (data.province) {
                const provinceName = data.province;
                if (provinceName) {
                  tatApiSvc.getProvince(provinceName).then((response) => {
                    const p = response.data?.data;
                    if (p) {
                      this.blogForm.regionId = p.regionId;
                      this.blogForm.provinceCode = p.code;
                    }
                  });
                }
              }

              this.hiddenButtonSave = false;
            }
          }
        })
        .catch((err) => {
          console.log(err);
          if (!this.blogForm.uuid) {
            toast.error("data not found", {
              autoClose: localStorage.getItem("toastCountdown"),
            });
            this.$router.push(`../tat/list`);
          }
        });
    },
    uploadThumbnail() {
      const formData = new FormData();

      for (const element of this.$refs.thumbnail.files) {
        let file = element;
        formData.append("images", file);
      }

      blogSvc
        .uploadFile(formData)
        .then((resp) => {
          this.blogForm.thumbnail = resp.data.data[0].url;
        })
        .finally(() => {
          this.$refs.thumbnail.value = "";
        });
    },
    onEditorReady(editor) {
      editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
        return new MyUploadAdapter(loader);
      };
    },
    uploadImage() {
      const formData = new FormData();

      for (const element of this.$refs.file.files) {
        let file = element;
        formData.append("images", file);
      }

      blogSvc
        .uploadFile(formData)
        .then((resp) => {
          resp.data.data.forEach((image) => {
            this.blogForm.images.push({ uuid: image.url, url: image.url });
          });
        })
        .finally(() => {
          this.$refs.file.value = "";
        });
    },
    handleApprove() {
      this.submitted = true;
      this.reject.remark = "manage-blog";
      this.requestEdit.remark = "manage-blog";
      const data = {
        blogUUID: this.blogForm.uuid,
        status: "approved",
        isPublish: true,
        remark: this.approve.remark,
      };

      blogSvc
        .verifyBlog(data)
        .catch((err) => {
          toast.error(err, {
            autoClose: localStorage.getItem("toastCountdown"),
          });
        })
        .finally(() => {
          this.showModalApprove = false;
          this.submitted = false;
          toast.success("Approve Successfully", {
            autoClose: localStorage.getItem("toastCountdown"),
          });
          this.$router.push("list");
        });
    },
    handleReject() {
      this.requestEdit.remark = "manage-blog";
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      } else {
        const data = {
          blogUUID: this.blogForm.uuid,
          status: "rejected",
          isPublish: false,
          remark: this.reject.remark,
        };

        blogSvc
          .verifyBlog(data)
          .catch((err) => {
            toast.error(err, {
              autoClose: localStorage.getItem("toastCountdown"),
            });
          })
          .finally(() => {
            this.showModalReject = false;
            this.submitted = false;
            toast.success("Reject Successfully", {
              autoClose: localStorage.getItem("toastCountdown"),
            });
            this.$router.push("list");
          });
      }
    },
    handleSubmit() {
      this.submitted = true;
      this.reject.remark = "manage-blog";
      this.requestEdit.remark = "manage-blog";
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      } else {
        this.submitted = false;

        if (this.submitted) {
          return;
        }

        let tags = [];
        if (this.blogForm.tag) {
          tags = this.blogForm.tag.map((tag) => {
            return tag;
          });
        }

        const images = [];
        Object.keys(this.blogForm.images).forEach((key) => {
          images.push({
            priority: +key + 1,
            url: this.blogForm.images[key].url,
            originName: this.blogForm.images[key].originName,
          });
        });

        this.blogForm.facebookName = this.isFacebook
          ? this.blogForm.facebookName
          : "";
        this.blogForm.facebookLink = this.isFacebook
          ? this.blogForm.facebookLink
          : "";
        this.blogForm.instagramName = this.isInstagram
          ? this.blogForm.instagramName
          : "";
        this.blogForm.instagramLink = this.isInstagram
          ? this.blogForm.instagramLink
          : "";
        this.blogForm.youtubeName = this.isYoutube
          ? this.blogForm.youtubeName
          : "";
        this.blogForm.youtubeLink = this.isYoutube
          ? this.blogForm.youtubeLink
          : "";
        this.blogForm.websiteName = this.isWebsite
          ? this.blogForm.websiteName
          : "";
        this.blogForm.websiteLink = this.isWebsite
          ? this.blogForm.websiteLink
          : "";
        this.blogForm.phone = this.isPhone ? this.blogForm.phone : "";

        const data = {
          ...this.blogForm,
          categoryUUID: this.blogForm.category,
          festivalUUID: this.blogForm.festivalUUID,
          startDate: this.blogForm.startDate
            ? DateTime.fromJSDate(this.blogForm.startDate).toFormat(
                "yyyy-MM-dd"
              )
            : "",
          endDate: this.blogForm.endDate
            ? DateTime.fromJSDate(this.blogForm.endDate).toFormat("yyyy-MM-dd")
            : "",
          tags,
          images,
        };

        if (this.blogForm.uuid) {
          Object.assign(data, { blogUUID: this.blogForm.uuid });
          blogSvc
            .updateBlog(data)
            .catch((err) => {
              toast.error(err, {
                autoClose: localStorage.getItem("toastCountdown"),
              });
            })
            .finally(() => {
              this.submitted = false;
              this.get(this.blogForm.uuid);
              toast.success("Updated Successfully", {
                autoClose: localStorage.getItem("toastCountdown"),
              });

              if (this.isSendApprove) {
                this.sendApprove();
              }
            });
        } else {
          blogSvc
            .createBlog(data)
            .then((resp) => {
              this.blogForm.uuid = resp.data.data;
            })
            .catch((err) => {
              toast.error(err, {
                autoClose: localStorage.getItem("toastCountdown"),
              });
            })
            .finally(() => {
              toast.success("Created Successfully", {
                autoClose: localStorage.getItem("toastCountdown"),
              });
              this.submitted = false;

              if (this.isSendApprove) {
                this.sendApprove();
              } else {
                this.$router.push("list");
              }
            });
        }
      }
    },
    sendApprove() {
      const data = { blogUUID: this.blogForm.uuid };
      blogSvc
        .sendApproveBlog(data)
        .catch((err) => {
          toast.error(err, {
            autoClose: localStorage.getItem("toastCountdown"),
          });
        })
        .finally(() => {
          toast.success("Send Approve Successfully", {
            autoClose: localStorage.getItem("toastCountdown"),
          });
          this.$router.push("list");
        });
    },
    handleRequestEdit() {
      this.reject.remark = "manage-blog";
      // this.submitted = true;
      // this.v$.$touch();
      // if (this.v$.$invalid) {
      //   return;
      // } else {
        this.submitted = false;
        const data = {
          blogUUID: this.blogForm.uuid,
          remark: this.requestEdit.remark,
        };

        let tempUuid = null;
        blogSvc
          .requestEditBlog(data)
          .then((resp) => {
            tempUuid = resp.data.data;
          })
          .catch((err) => {
            toast.error(err, {
              autoClose: localStorage.getItem("toastCountdown"),
            });
          })
          .finally(() => {
            this.showModalRequestEdit = false;
            toast.success("Request Edit Successfully", {
              autoClose: localStorage.getItem("toastCountdown"),
            });

            this.$router.push(`manage-edit?uuid=${tempUuid}`);
          });
      // }
    },
    deleteImage(index) {
      this.blogForm.images.splice(index, true);
    },
    isNumber: function (inputType, evt) {
      if (inputType === "number") {
        evt = evt ? evt : window.event;
        const charCode = evt.which ? evt.which : evt.keyCode;
        if (
          charCode > 31 &&
          (charCode < 48 || charCode > 57) &&
          charCode !== 46 &&
          ![37, 38, 39, 40].includes(charCode)
        ) {
          evt.preventDefault();
        } else {
          return true;
        }
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="card">
        <div class="card-body">
          <!-- MODAL APPROVE -->
          <div class="text-sm-end">
            <b-modal
              v-model="showModalApprove"
              title="Approve"
              title-class="text-black font-18"
              body-class="p-3"
              hide-footer
              no-close-on-backdrop
            >
              <form @submit.prevent="handleApprove">
                <div class="row">
                  <div class="col-12">
                    <div class="mb-3">
                      <label for="">Remark</label>
                      <input
                        id="remark"
                        v-model="approve.remark"
                        type="text"
                        class="form-control"
                      />
                    </div>
                  </div>
                </div>
                <div class="text-end pt-5 mt-3">
                  <b-button variant="light" @click="showModalApprove = false"
                    >Close</b-button
                  >
                  <b-button type="submit" variant="success" class="ms-1"
                    >Approve</b-button
                  >
                </div>
              </form>
            </b-modal>
          </div>
          <!-- MODAL APPROVE -->

          <!-- MODAL REJECT -->
          <div class="text-sm-end">
            <b-modal
              v-model="showModalReject"
              title="Reject"
              title-class="text-black font-18"
              body-class="p-3"
              hide-footer
              no-close-on-backdrop
            >
              <form @submit.prevent="handleReject">
                <div class="row">
                  <div class="col-12">
                    <div class="mb-3">
                      <label for="">Remark</label>
                      <input
                        id="remark"
                        v-model="reject.remark"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid': submitted && v$.reject.remark.$error,
                        }"
                      />

                      <div
                        v-if="submitted && v$.reject.remark.$error"
                        class="invalid-feedback"
                      >
                        <span v-if="v$.reject.remark.required.$message">{{
                          v$.reject.remark.required.$message
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="text-end pt-5 mt-3">
                  <b-button variant="light" @click="showModalReject = false"
                    >Close</b-button
                  >
                  <b-button type="submit" variant="danger" class="ms-1"
                    >Reject</b-button
                  >
                </div>
              </form>
            </b-modal>
          </div>
          <!-- MODAL REJECT -->

          <!-- MODAL REQUEST EDIT -->
          <div class="text-sm-end">
            <b-modal
              v-model="showModalRequestEdit"
              title="Request Edit"
              title-class="text-black font-18"
              body-class="p-3"
              hide-footer
              no-close-on-backdrop
            >
              <form @submit.prevent="handleRequestEdit">
                <div class="row">
                  <div class="col-12">
                    <div class="mb-3">
                      <label for="remark">Remark</label>
                      <input
                        id="remark"
                        v-model="requestEdit.remark"
                        type="text"
                        class="form-control"
                      />
                      <!-- :class="{
                          'is-invalid':
                            submitted && v$.requestEdit.remark.$error,
                      }" -->
                      <!-- <div
                        v-if="submitted && v$.requestEdit.remark.$error"
                        class="invalid-feedback"
                      >
                        <span v-if="v$.requestEdit.remark.required.$message">{{
                          v$.requestEdit.remark.required.$message
                        }}</span>
                      </div> -->
                    </div>
                  </div>
                </div>
                <div class="text-end pt-5 mt-3">
                  <b-button
                    variant="light"
                    @click="showModalRequestEdit = false"
                    >Close</b-button
                  >
                  <b-button type="submit" variant="warning" class="ms-1"
                    >Request</b-button
                  >
                </div>
              </form>
            </b-modal>
          </div>
          <!-- MODAL REQUEST EDIT -->

          <form
            class="needs-validation"
            @submit.prevent="
              handleSubmit();
              isSendApprove = false;
            "
          >
            <div class="row">
              <div class="col-md-3">
                <div class="mb-3 position-relative">
                  <label for="">Type <span class="is-required">*</span></label>
                  <Multiselect
                    :disabled="isDisabled"
                    v-model="blogForm.type"
                    mode="single"
                    :searchable="true"
                    placeholder="Choose..."
                    :options="typesOption"
                    :class="{
                      'is-invalid': submitted && v$.blogForm.type.$error,
                    }"
                  />
                  <div
                    v-if="submitted && v$.blogForm.type.$error"
                    class="invalid-feedback"
                  >
                    <span v-if="v$.blogForm.type.required.$message">{{
                      v$.blogForm.type.required.$message
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div class="row">
              <BRow>
                <BCol lg="6">
                  <BCard no-body>
                    <BCardBody>
                      <BRow class="justify-content-center">
                        <BCol xl="12">
                          <div>
                            <div class="form-check font-size-16">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="blogForm.isTh"
                                :disabled="isDisabled || (!blogForm.isEn && blogForm.isTh)"
                              />
                              <label class="">TH</label>
                            </div>
                            <div class="row" v-if="blogForm.isTh">
                              <div class="col-md-9">
                                <div class="mb-3 position-relative">
                                  <label
                                    >Event Name
                                    <span class="is-required">*</span></label
                                  >
                                  <input
                                    :disabled="isDisabled"
                                    v-model="blogForm.name"
                                    type="text"
                                    class="form-control"
                                    :class="{
                                      'is-invalid':
                                        submitted && v$.blogForm.name.$error,
                                    }"
                                  />
                                  <div
                                    v-if="submitted && v$.blogForm.name.$error"
                                    class="invalid-feedback"
                                  >
                                    <span
                                      v-if="v$.blogForm.name.required.$message"
                                      >{{
                                        v$.blogForm.name.required.$message
                                      }}</span
                                    >
                                  </div>
                                </div>
                              </div>
                              <div class="col-md-9">
                                <div class="mb-3 position-relative">
                                  <label
                                    >Location Display Name
                                    <span
                                      v-if="blogForm.type === 'event'"
                                      class="is-required"
                                      >*</span
                                    ></label
                                  >
                                  <input
                                    :disabled="isDisabled"
                                    v-model="blogForm.location"
                                    type="text"
                                    class="form-control"
                                    :class="{
                                      'is-invalid':
                                        submitted &&
                                        blogForm.type === 'event' &&
                                        v$.blogForm.location.$error,
                                    }"
                                  />
                                  <div
                                    v-if="
                                      submitted &&
                                      blogForm.type === 'event' &&
                                      v$.blogForm.location.$error
                                    "
                                    class="invalid-feedback"
                                  >
                                    <span
                                      v-if="
                                        v$.blogForm.location.required.$message
                                      "
                                      >{{
                                        v$.blogForm.location.required.$message
                                      }}</span
                                    >
                                  </div>
                                </div>
                              </div>
                              <div class="col-md-12">
                                <div class="mb-3 position-relative">
                                  <label
                                    >Location Link
                                    <span
                                      v-if="blogForm.type === 'event'"
                                      class="is-required"
                                      >*</span
                                    ></label
                                  >
                                  <input
                                    :disabled="isDisabled"
                                    v-model="blogForm.locationLink"
                                    type="text"
                                    class="form-control"
                                    :class="{
                                      'is-invalid':
                                        submitted &&
                                        blogForm.type === 'event' &&
                                        v$.blogForm.locationLink.$error,
                                    }"
                                  />
                                  <div
                                    v-if="
                                      submitted &&
                                      blogForm.type === 'event' &&
                                      v$.blogForm.locationLink.$error
                                    "
                                    class="invalid-feedback"
                                  >
                                    <span
                                      v-if="
                                        v$.blogForm.locationLink.required
                                          .$message
                                      "
                                      >{{
                                        v$.blogForm.locationLink.required
                                          .$message
                                      }}</span
                                    >
                                  </div>
                                </div>
                              </div>
                              <div class="col-md-12">
                                <div
                                  class="mb-3 position-relative ckeditor"
                                  :class="{
                                    'is-invalid':
                                      submitted && v$.blogForm.detail.$error,
                                  }"
                                >
                                  <label
                                    >Description
                                    <span class="is-required">*</span></label
                                  >
                                  <ckeditor
                                    :disabled="isDisabled"
                                    v-model="blogForm.detail"
                                    :editor="editor"
                                    :config="editorConfig"
                                    @ready="onEditorReady"
                                    :class="{
                                      'is-invalid':
                                        submitted && v$.blogForm.detail.$error,
                                    }"
                                  ></ckeditor>
                                  <div
                                    v-if="
                                      submitted && v$.blogForm.detail.$error
                                    "
                                    class="invalid-feedback"
                                  >
                                    <span
                                      v-if="
                                        v$.blogForm.detail.required.$message
                                      "
                                      >{{
                                        v$.blogForm.detail.required.$message
                                      }}</span
                                    >
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </BCol>
                      </BRow>
                    </BCardBody>
                  </BCard>
                </BCol>
                <BCol lg="6">
                  <BCard no-body>
                    <BCardBody>
                      <BRow class="justify-content-center">
                        <BCol xl="12">
                          <div>
                            <div class="form-check font-size-16">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="blogForm.isEn"
                                :disabled="isDisabled"
                              />
                              <label class="">EN</label>
                            </div>
                            <div class="row" v-if="blogForm.isEn">
                              <div class="col-md-9">
                                <div class="mb-3 position-relative">
                                  <label
                                    >Event Name
                                    <span class="is-required">*</span></label
                                  >
                                  <input
                                    :disabled="isDisabled"
                                    v-model="blogForm.nameEn"
                                    type="text"
                                    class="form-control"
                                    :class="{
                                      'is-invalid':
                                        submitted && v$.blogForm.nameEn.$error,
                                    }"
                                  />
                                  <div
                                    v-if="
                                      submitted && v$.blogForm.nameEn.$error
                                    "
                                    class="invalid-feedback"
                                  >
                                    <span
                                      v-if="
                                        v$.blogForm.nameEn.required.$message
                                      "
                                      >{{
                                        v$.blogForm.nameEn.required.$message
                                      }}</span
                                    >
                                  </div>
                                </div>
                              </div>
                              <div class="col-md-9">
                                <div class="mb-3 position-relative">
                                  <label
                                    >Location Display Name
                                    <span
                                      v-if="blogForm.type === 'event'"
                                      class="is-required"
                                      >*</span
                                    ></label
                                  >
                                  <input
                                    :disabled="isDisabled"
                                    v-model="blogForm.locationEn"
                                    type="text"
                                    class="form-control"
                                    :class="{
                                      'is-invalid':
                                        submitted &&
                                        blogForm.type === 'event' &&
                                        v$.blogForm.locationEn.$error,
                                    }"
                                  />
                                  <div
                                    v-if="
                                      submitted &&
                                      blogForm.type === 'event' &&
                                      v$.blogForm.locationEn.$error
                                    "
                                    class="invalid-feedback"
                                  >
                                    <span
                                      v-if="
                                        v$.blogForm.locationEn.required.$message
                                      "
                                      >{{
                                        v$.blogForm.locationEn.required.$message
                                      }}</span
                                    >
                                  </div>
                                </div>
                              </div>
                              <div class="col-md-12">
                                <div class="mb-3 position-relative">
                                  <label
                                    >Location Link
                                    <span
                                      v-if="blogForm.type === 'event'"
                                      class="is-required"
                                      >*</span
                                    ></label
                                  >
                                  <input
                                    :disabled="isDisabled"
                                    v-model="blogForm.locationLinkEn"
                                    type="text"
                                    class="form-control"
                                    :class="{
                                      'is-invalid':
                                        submitted &&
                                        blogForm.type === 'event' &&
                                        v$.blogForm.locationLinkEn.$error,
                                    }"
                                  />
                                  <div
                                    v-if="
                                      submitted &&
                                      blogForm.type === 'event' &&
                                      v$.blogForm.locationLinkEn.$error
                                    "
                                    class="invalid-feedback"
                                  >
                                    <span
                                      v-if="
                                        v$.blogForm.locationLinkEn.required
                                          .$message
                                      "
                                      >{{
                                        v$.blogForm.locationLinkEn.required
                                          .$message
                                      }}</span
                                    >
                                  </div>
                                </div>
                              </div>
                              <div class="col-md-12">
                                <div
                                  class="mb-3 position-relative ckeditor"
                                  :class="{
                                    'is-invalid':
                                      submitted && v$.blogForm.detailEn.$error,
                                  }"
                                >
                                  <label
                                    >Description
                                    <span class="is-required">*</span></label
                                  >
                                  <ckeditor
                                    :disabled="isDisabled"
                                    v-model="blogForm.detailEn"
                                    :editor="editor"
                                    :config="editorConfig"
                                    @ready="onEditorReady"
                                    :class="{
                                      'is-invalid':
                                        submitted &&
                                        v$.blogForm.detailEn.$error,
                                    }"
                                  ></ckeditor>
                                  <div
                                    v-if="
                                      submitted && v$.blogForm.detailEn.$error
                                    "
                                    class="invalid-feedback"
                                  >
                                    <span
                                      v-if="
                                        v$.blogForm.detailEn.required.$message
                                      "
                                      >{{
                                        v$.blogForm.detailEn.required.$message
                                      }}</span
                                    >
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </BCol>
                      </BRow>
                    </BCardBody>
                  </BCard>
                </BCol>
              </BRow>
            </div>
            <hr />
            <div class="row">
              <div class="col-md-3">
                <div class="mb-3 position-relative">
                  <label for=""
                    >Category <span class="is-required">*</span></label
                  >
                  <Multiselect
                    :disabled="isDisabled"
                    v-model="blogForm.category"
                    mode="single"
                    :searchable="true"
                    placeholder="Choose..."
                    :options="categoriesOption"
                    :class="{
                      'is-invalid': submitted && v$.blogForm.category.$error,
                    }"
                  />
                  <div
                    v-if="submitted && v$.blogForm.category.$error"
                    class="invalid-feedback"
                  >
                    <span v-if="v$.blogForm.category.required.$message">{{
                      v$.blogForm.category.required.$message
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="mb-3 position-relative">
                  <label
                    >Festival
                    </label
                  >
                  <Multiselect
                    :disabled="isDisabled"
                    v-model="blogForm.festivalUUID"
                    mode="single"
                    :searchable="true"
                    placeholder="Choose..."
                    :options="festivalsOption"
                  />
                </div>
              </div>
              <div class="col-md-3">
                <div class="mb-3 position-relative">
                  <label
                    >Region
                    <span v-if="blogForm.type === 'event'" class="is-required"
                      >*</span
                    ></label
                  >
                  <Multiselect
                    :disabled="isDisabled"
                    v-model="blogForm.regionId"
                    mode="single"
                    :searchable="true"
                    placeholder="Choose..."
                    :options="regionsOption"
                    @change="getProvince($event)"
                    :class="{
                      'is-invalid':
                        submitted &&
                        blogForm.type === 'event' &&
                        v$.blogForm.regionId.$error,
                    }"
                  />
                  <div
                    v-if="
                      submitted &&
                      blogForm.type === 'event' &&
                      v$.blogForm.regionId.$error
                    "
                    class="invalid-feedback"
                  >
                    <span v-if="v$.blogForm.regionId.required.$message">{{
                      v$.blogForm.regionId.required.$message
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="mb-3 position-relative">
                  <label
                    >Province
                    <span v-if="blogForm.type === 'event'" class="is-required"
                      >*</span
                    ></label
                  >
                  <Multiselect
                    :disabled="isDisabled || blogForm.regionId === ''"
                    v-model="blogForm.provinceCode"
                    mode="single"
                    :searchable="true"
                    placeholder="Choose..."
                    :options="provincesOption"
                    :class="{
                      'is-invalid':
                        submitted &&
                        blogForm.type === 'event' &&
                        v$.blogForm.provinceCode.$error,
                    }"
                  />
                  <div
                    v-if="
                      submitted &&
                      blogForm.type === 'event' &&
                      v$.blogForm.provinceCode.$error
                    "
                    class="invalid-feedback"
                  >
                    <span v-if="v$.blogForm.provinceCode.required.$message">{{
                      v$.blogForm.provinceCode.required.$message
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3 position-relative">
                  <label>Office</label>
                  <Multiselect
                    :disabled="isDisabled"
                    v-model="blogForm.officeUUID"
                    mode="single"
                    :searchable="true"
                    placeholder="Choose..."
                    :options="officesOption"
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-9">
                <div class="mb-3 position-relative">
                  <label for="">Tag</label>
                  <Multiselect
                    :disabled="isDisabled"
                    v-model="blogForm.tag"
                    mode="tags"
                    :close-on-select="false"
                    :searchable="true"
                    placeholder="Choose..."
                    :options="tagsOptions"
                  />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-3">
                <div class="mb-3 position-relative">
                  <label for="">
                    Start Date
                    <span v-if="blogForm.type === 'event'" class="is-required"
                      >*</span
                    >
                  </label>
                  <datepicker
                    :disabled="isDisabled"
                    ref="startDate"
                    v-model="blogForm.startDate"
                    lang="en"
                    class="form-control"
                    :class="{
                      'is-invalid':
                        submitted &&
                        blogForm.type === 'event' &&
                        v$.blogForm.startDate.$error,
                    }"
                  >
                  </datepicker>
                  <div
                    v-if="
                      submitted &&
                      blogForm.type === 'event' &&
                      !blogForm.startDate
                    "
                    class="invalid-feedback-custom"
                  >
                    <span v-if="v$.blogForm.startDate.required.$message">{{
                      v$.blogForm.startDate.required.$message
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="mb-3 position-relative">
                  <label for="">
                    End Date
                    <span v-if="blogForm.type === 'event'" class="is-required"
                      >*</span
                    >
                  </label>
                  <datepicker
                    :disabled="isDisabled"
                    v-model="blogForm.endDate"
                    :lower-limit="lowerDate"
                    lang="en"
                    class="form-control"
                    :class="{
                      'is-invalid':
                        submitted &&
                        blogForm.type === 'event' &&
                        v$.blogForm.endDate.$error,
                    }"
                  ></datepicker>
                  <div
                    v-if="
                      submitted &&
                      blogForm.type === 'event' &&
                      !blogForm.endDate
                    "
                    class="invalid-feedback-custom"
                  >
                    <span v-if="v$.blogForm.endDate.required.$message">{{
                      v$.blogForm.endDate.required.$message
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <label for="">Date Remark</label>
                <input
                  :disabled="isDisabled"
                  v-model="blogForm.dateRemark"
                  type="text"
                  class="form-control"
                />
              </div>
              <div v-if="blogForm.type === 'event'" class="col-md-3">
                <label for="">Start Time</label>
                <input
                  :disabled="isDisabled"
                  v-model="blogForm.startTime"
                  type="time"
                  class="form-control"
                />
              </div>
              <div v-if="blogForm.type === 'event'" class="col-md-3">
                <label for="">End Time</label>
                <input
                  :disabled="isDisabled"
                  v-model="blogForm.endTime"
                  type="time"
                  class="form-control"
                />
              </div>
              <div v-if="blogForm.type === 'event'" class="col-md-3">
                <div class="mb-3 position-relative">
                  <label for=""
                    >Ticket Type <span class="is-required">*</span></label
                  >
                  <BFormGroup>
                    <BFormRadio
                      v-model="blogForm.ticketType"
                      class="form-check-right me-2 align-middle"
                      value="free"
                      :disabled="isDisabled"
                      name="ticket_type"
                      plain
                      :class="{
                        'is-invalid':
                          submitted && v$.blogForm.ticketType.$error,
                      }"
                      >Free</BFormRadio
                    >
                    <BFormRadio
                      v-model="blogForm.ticketType"
                      class="form-check-right me-2 align-middle"
                      value="pay"
                      :disabled="isDisabled"
                      name="ticket_type"
                      plain
                      :class="{
                        'is-invalid':
                          submitted && v$.blogForm.ticketType.$error,
                      }"
                      >Pay For Expenses</BFormRadio
                    >
                    <div
                      v-if="submitted && v$.blogForm.ticketType.$error"
                      class="invalid-feedback"
                    >
                      <span v-if="v$.blogForm.ticketType.required.$message">{{
                        v$.blogForm.ticketType.required.$message
                      }}</span>
                    </div>
                  </BFormGroup>
                </div>
              </div>
              <div v-if="blogForm.type === 'event'" class="col-md-6">
                <label for="">Ticket Link</label>
                <input
                  :disabled="isDisabled"
                  v-model="blogForm.ticketLink"
                  type="text"
                  class="form-control"
                />
              </div>
              <hr class="mt-4" v-if="blogForm.type === 'event'" />
              <div class="col-md-2" v-if="blogForm.type === 'event'">
                <div class="form-check font-size-16">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isFacebook"
                    :disabled="isDisabled"
                  />
                  <label class="">Facebook</label>
                </div>
                <div class="row" v-if="isFacebook">
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Display Name</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.facebookName"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isFacebook &&
                            v$.blogForm.facebookName.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted &&
                          this.isFacebook &&
                          v$.blogForm.facebookName.$error
                        "
                        class="invalid-feedback"
                      >
                        <span
                          v-if="v$.blogForm.facebookName.required.$message"
                          >{{
                            v$.blogForm.facebookName.required.$message
                          }}</span
                        >
                      </div>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Link</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.facebookLink"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isFacebook &&
                            v$.blogForm.facebookLink.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted &&
                          this.isFacebook &&
                          v$.blogForm.facebookLink.$error
                        "
                        class="invalid-feedback"
                      >
                        <span
                          v-if="v$.blogForm.facebookLink.required.$message"
                          >{{
                            v$.blogForm.facebookLink.required.$message
                          }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-2" v-if="blogForm.type === 'event'">
                <div class="form-check font-size-16">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isInstagram"
                    :disabled="isDisabled"
                  />
                  <label class="">Instagram</label>
                </div>
                <div class="row" v-if="isInstagram">
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Display Name</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.instagramName"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isInstagram &&
                            v$.blogForm.instagramName.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted &&
                          this.isInstagram &&
                          v$.blogForm.instagramName.$error
                        "
                        class="invalid-feedback"
                      >
                        <span
                          v-if="v$.blogForm.instagramName.required.$message"
                          >{{
                            v$.blogForm.instagramName.required.$message
                          }}</span
                        >
                      </div>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Link</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.instagramLink"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isInstagram &&
                            v$.blogForm.instagramLink.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted &&
                          this.isInstagram &&
                          v$.blogForm.instagramLink.$error
                        "
                        class="invalid-feedback"
                      >
                        <span
                          v-if="v$.blogForm.instagramLink.required.$message"
                          >{{
                            v$.blogForm.instagramLink.required.$message
                          }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-2" v-if="blogForm.type === 'event'">
                <div class="form-check font-size-16">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isYoutube"
                    :disabled="isDisabled"
                  />
                  <label class="">Youtube</label>
                </div>
                <div class="row" v-if="isYoutube">
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Display Name</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.youtubeName"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isYoutube &&
                            v$.blogForm.youtubeName.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted &&
                          this.isYoutube &&
                          v$.blogForm.youtubeName.$error
                        "
                        class="invalid-feedback"
                      >
                        <span
                          v-if="v$.blogForm.youtubeName.required.$message"
                          >{{ v$.blogForm.youtubeName.required.$message }}</span
                        >
                      </div>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Link</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.youtubeLink"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isYoutube &&
                            v$.blogForm.youtubeLink.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted &&
                          this.isYoutube &&
                          v$.blogForm.youtubeLink.$error
                        "
                        class="invalid-feedback"
                      >
                        <span
                          v-if="v$.blogForm.youtubeLink.required.$message"
                          >{{ v$.blogForm.youtubeLink.required.$message }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-2" v-if="blogForm.type === 'event'">
                <div class="form-check font-size-16">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isWebsite"
                    :disabled="isDisabled"
                  />
                  <label class="">Website</label>
                </div>
                <div class="row" v-if="isWebsite">
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Display Name</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.websiteName"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isWebsite &&
                            v$.blogForm.websiteName.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted &&
                          this.isWebsite &&
                          v$.blogForm.websiteName.$error
                        "
                        class="invalid-feedback"
                      >
                        <span
                          v-if="v$.blogForm.websiteName.required.$message"
                          >{{ v$.blogForm.websiteName.required.$message }}</span
                        >
                      </div>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Link</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.websiteLink"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isWebsite &&
                            v$.blogForm.websiteLink.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted &&
                          this.isWebsite &&
                          v$.blogForm.websiteLink.$error
                        "
                        class="invalid-feedback"
                      >
                        <span
                          v-if="v$.blogForm.websiteLink.required.$message"
                          >{{ v$.blogForm.websiteLink.required.$message }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-2" v-if="blogForm.type === 'event'">
                <div class="form-check font-size-16">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isPhone"
                    :disabled="isDisabled"
                  />
                  <label class="">Phone</label>
                </div>
                <div class="row" v-if="isPhone">
                  <div class="col-md-12">
                    <div class="mb-3 position-relative">
                      <label for="">Phone Number</label>
                      <input
                        :disabled="isDisabled"
                        v-model="blogForm.phone"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid':
                            submitted &&
                            this.isPhone &&
                            v$.blogForm.phone.$error,
                        }"
                      />
                      <div
                        v-if="
                          submitted && this.isPhone && v$.blogForm.phone.$error
                        "
                        class="invalid-feedback"
                      >
                        <span v-if="v$.blogForm.phone.required.$message">{{
                          v$.blogForm.phone.required.$message
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr class="mt-4" />
              <div class="col-md-3">
                <div class="mb-3 position-relative">
                  <label for=""
                    >Thumbnail <span class="is-required">*</span></label
                  >
                  <div v-if="blogForm.thumbnail" class="upload-thumbnail">
                    <img alt="thumbnail" :src="blogForm.thumbnail" />
                  </div>
                  <input
                    :disabled="isDisabled"
                    id="thumbnail"
                    ref="thumbnail"
                    type="file"
                    class="form-control"
                    :class="{
                      'is-invalid': submitted && v$.blogForm.thumbnail.$error,
                    }"
                    accept="image/x-png,image/gif,image/jpeg"
                    @change="uploadThumbnail"
                  />
                  <div
                    v-if="submitted && v$.blogForm.thumbnail.$error"
                    class="invalid-feedback"
                  >
                    <span v-if="v$.blogForm.thumbnail.required.$message">{{
                      v$.blogForm.thumbnail.required.$message
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col-md-12">
                <draggable
                  v-model="blogForm.images"
                  item-key="id"
                  :disabled="isDisabled"
                >
                  <template #item="{ element, index }">
                    <div class="album-drag row">
                      <div class="col-md-12 image">
                        <img :alt="element.uuid" :src="element.url" />
                      </div>
                      <div class="col-md-12 btn-delete">
                        <b-button
                          v-show="!isDisabled"
                          size="sm"
                          variant="danger"
                          @click="deleteImage(index)"
                          >Delete</b-button
                        >
                      </div>
                    </div>
                  </template>
                </draggable>
              </div>
              <div class="col-md-12">
                <div class="mb-3 position-relative">
                  <label for="">Album</label>
                  <input
                    id="remark"
                    type="file"
                    ref="file"
                    multiple
                    accept="image/x-png,image/gif,image/jpeg"
                    @change="uploadImage"
                    :disabled="isDisabled"
                    class="form-control"
                  />
                  <!-- <div
                    v-if="submitted && v$.blogForm.images.$error"
                    class="invalid-feedback"
                  >
                    <span v-if="v$.blogForm.images.required.$message">{{
                      v$.blogForm.images.required.$message
                    }}</span>
                  </div> -->
                </div>
              </div>
            </div>
            <div class="text-sm-end">
              <button
                :hidden="hiddenButtonSave"
                class="btn btn-success ms-1"
                type="submit"
              >
                {{ blogForm.uuid !== null ? "Update" : "Save" }}
              </button>
              <b-button
                :hidden="hiddenButtonSave"
                class="btn btn-warning ms-1"
                @click="
                  isSendApprove = true;
                  this.handleSubmit();
                "
              >
                {{ blogForm.uuid !== null ? "Update" : "Save" }} And Send
                Approve
              </b-button>
              <b-button
                v-show="
                  role !== 'user' && blogForm.statusCode === 'pending_verify'
                "
                class="btn btn-success ms-1"
                a
                @click="showModalApprove = true"
              >
                Approve
              </b-button>
              <b-button
                v-show="
                  role !== 'user' && blogForm.statusCode === 'pending_verify'
                "
                class="btn btn-danger ms-1"
                @click="
                  showModalReject = true;
                  reject.remark = '';
                "
              >
                Reject
              </b-button>
              <b-button
                v-show="
                  ['approved', 'publish', 'unpublish'].includes(
                    blogForm.statusCode
                  )
                "
                class="btn btn-warning ms-1"
                @click="
                  showModalRequestEdit = true;
                  requestEdit.remark = '';
                "
              >
                Request Edit
              </b-button>
              <div
                v-if="
                  blogForm.uuid !== null &&
                  !hiddenButtonSave &&
                  blogForm.tatApiChangeData
                "
                class="mt-2 mb-2"
              >
                <i class="bx bx bxs-error-circle text-danger"
                  >ข้อมูลจาก TAT มีการเปลี่ยนแปลง โปรดตรวจสอบก่อนบันทึก</i
                >
              </div>
            </div>
          </form>
          <div class="row">
            <div class="col-xl-6">
              <div class="card">
                <div class="card-body">
                  <h4 class="card-title">Status History</h4>
                  <div class="table-responsive">
                    <table class="table table-striped mb-0">
                      <thead>
                        <tr>
                          <th id="history-no">#</th>
                          <th id="history-status">Status</th>
                          <th id="history-username">Username</th>
                          <th id="history-remark">Remark</th>
                          <th id="history-created">Created Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(history, index) of blogForm.statusHistory"
                          :key="history.index"
                        >
                          <th scope="row">{{ index + 1 }}</th>
                          <td>
                            <b-badge
                              v-if="
                                [
                                  'created',
                                  'pending_verify',
                                  'editing',
                                  'draft',
                                  'updated',
                                ].includes(history.statusCode)
                              "
                              variant="warning"
                              >{{ history.statusText }}</b-badge
                            >
                            <b-badge
                              v-else-if="
                                ['rejected', 'unpublish'].includes(
                                  history.statusCode
                                )
                              "
                              variant="danger"
                              >{{ history.statusText }}</b-badge
                            >
                            <b-badge
                              v-else-if="
                                [
                                  'approved',
                                  'publish',
                                  'publish_draft',
                                ].includes(history.statusCode)
                              "
                              variant="success"
                              >{{ history.statusText }}</b-badge
                            >
                            <b-badge v-else>-</b-badge>
                          </td>
                          <td>{{ history.username }}</td>
                          <td>{{ history.remark ? history.remark : "-" }}</td>
                          <td>{{ history.createdAt }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-6">
              <div class="card">
                <div class="card-body">
                  <h4 class="card-title">Revisions</h4>
                  <div class="table-responsive">
                    <table class="table table-striped mb-0">
                      <thead>
                        <tr>
                          <th id="revisions-no">#</th>
                          <th id="revisions-name">Name</th>
                          <th id="revisions-status">Status</th>
                          <th id="revisions-created">Created Date</th>
                          <th id="revisions-updated">Updated Date</th>
                          <th id="revisions-manage"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(revisions, index) of blogForm.revisions"
                          :key="revisions.index"
                        >
                          <th scope="row">{{ index + 1 }}</th>
                          <td>{{ revisions.name }}</td>
                          <td class="btn-primary">
                            <b-badge
                              v-if="
                                [
                                  'created',
                                  'pending_verify',
                                  'editing',
                                  'draft',
                                  'updated',
                                ].includes(revisions.statusCode)
                              "
                              variant="warning"
                              >{{ revisions.statusText }}</b-badge
                            >
                            <b-badge
                              v-else-if="
                                ['rejected', 'unpublish'].includes(
                                  revisions.statusCode
                                )
                              "
                              variant="danger"
                              >{{ revisions.statusText }}</b-badge
                            >
                            <b-badge
                              v-else-if="
                                [
                                  'approved',
                                  'publish',
                                  'publish_draft',
                                ].includes(revisions.statusCode)
                              "
                              variant="success"
                              >{{ revisions.statusText }}</b-badge
                            >
                            <b-badge v-else>-</b-badge>
                          </td>
                          <td>{{ revisions.createdAt }}</td>
                          <td>{{ revisions.updatedAt }}</td>
                          <td>
                            <router-link
                              :to="`manage-edit?uuid=${revisions.uuid}`"
                              target="_blank"
                              ><i
                                class="mdi mdi-eye font-size-16 align-middle"
                              ></i
                            ></router-link>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
<style>
.multiselect-tag {
  font-family: inherit !important;
}
.form-control[type="text"],
.form-control[type="number"],
.form-control[type="date"] {
  height: 40px !important;
}

.ckeditor.is-invalid .ck-blurred.ck.ck-content {
  border-color: #f46a6a !important;
  padding-right: calc(1.5em + 0.94rem) !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23f46a6a'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23f46a6a' stroke='none'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right calc(0.375em + 0.235rem) center !important;
  background-size: calc(0.75em + 0.47rem) calc(0.75em + 0.47rem) !important;
}

.multiselect.is-invalid {
  border-color: #f46a6a;
  padding-right: calc(1.5em + 0.94rem);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23f46a6a'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23f46a6a' stroke='none'/%3e%3c/svg%3e");

  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.235rem) center;
  background-size: calc(0.75em + 0.47rem) calc(0.75em + 0.47rem);
}
span.is-required {
  color: red;
}
.album-drag {
  display: inline-flex;
  margin: 10px;
  width: 245px !important;
  height: 300px !important;
}
.album-drag .image {
  text-align: center;
}
.album-drag .btn-delete {
  text-align: center;
  margin-top: 10px;
}
.album-drag img {
  max-width: 200px;
  max-height: 200px;
}
div.upload-thumbnail {
  width: auto;
  padding: 15px 0px;
}
div.upload-thumbnail img {
  max-width: 100%;
  height: auto;
}
div.upload-thumbnail .delete-attachment {
  color: red;
}
.invalid-feedback-custom {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f46a6a;
}
</style>
